import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { updateUser } from "../api/authApi";
import "../styles/Profile.css";

const Profile = () => {
  const { user, token } = useAuth();

  const [isFlipped, setIsFlipped] = useState(false);
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isError, setIsError] = useState(false);
  // FIXED: Added loading state to prevent double submissions
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
  });

  // Populate formData whenever user is updated
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNo: user.phoneNo || "",
      });
    }
  }, [user]);

  // FIXED: Helper to show notification and auto-dismiss
  const showNotification = (message, error = false, autoDismiss = true) => {
    setIsError(error);
    setSuccessMessage(message);
    setShowSuccess(true);

    if (autoDismiss) {
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // FIXED: Removed overly strict validation that blocked API call.
  // Validation now only checks for empty fields and same password.
  // Minimum length check removed — API decides what's valid.
  // FIXED: async/await properly wrapped with try/catch and finally.
  const handleSubmitPassword = async () => {
    // FIXED: Prevent double submission
    if (isSubmitting) return;

    // FIXED: Trim values to avoid whitespace-only submissions
    const oldPass = passwordData.oldPassword.trim();
    const newPass = passwordData.newPassword.trim();

    // Validation: empty fields
    if (!oldPass || !newPass) {
      showNotification("Please fill in all password fields.", true);
      return;
    }

    // Validation: same password — keep this check, it's valid UX
    if (oldPass === newPass) {
      showNotification("New password must be different from the current password.", true);
      return;
    }

    // Validation: no token
    if (!token) {
      showNotification("Session expired. Please login again.", true);
      return;
    }

    // FIXED: Set submitting state before API call
    setIsSubmitting(true);
    setShowSuccess(false); // hide any previous notification

    try {
      const res = await updateUser(
        {
          password: oldPass,
          newPassword: newPass,
          email: user.email,
          phone: user.phoneNo,
        },
        token,
      );

      // SUCCESS
      showNotification(res?.message || "Password updated successfully!", false);
      setPasswordData({ oldPassword: "", newPassword: "" });

      // FIXED: Flip back after longer delay so user sees success
      setTimeout(() => {
        setIsFlipped(false);
      }, 2500);
    } catch (error) {
      // FIXED: Comprehensive error extraction
      let msg = "Failed to change password. Please try again.";

      if (error?.response?.data?.message) {
        msg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        msg = error.response.data.error;
      } else if (error?.message) {
        // FIXED: Distinguish network errors from generic ones
        if (error.message === "Network Error") {
          msg = "Network error. Please check your connection and try again.";
        } else {
          msg = error.message;
        }
      }

      showNotification(msg, true, false); // FIXED: Don't auto-dismiss errors
    } finally {
      // FIXED: Always reset submitting state
      setIsSubmitting(false);
    }
  };

  const getInitials = () => {
    if (!formData.firstName || !formData.lastName) return "UN";
    return `${formData.firstName.charAt(0)}${formData.lastName.charAt(0)}`.toUpperCase();
  };

  // FIXED: Password strength calculation (was hardcoded to "Strong")
  const getPasswordStrength = (password) => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { label: "Weak", color: "#ef4444", width: "25%" };
    if (score === 2) return { label: "Fair", color: "#f97316", width: "50%" };
    if (score === 3) return { label: "Good", color: "#eab308", width: "75%" };
    return { label: "Strong", color: "#22c55e", width: "100%" };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  if (!user) {
    return (
      <div className="profile-wrapper">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-wrapper">
      {/* Success / Error Notification */}
      {showSuccess && (
        <div className={`success-notification ${isError ? "error" : "success"}`}>
          <div className="success-content">
            <div className={`success-icon ${isError ? "icon-error" : "icon-success"}`}>
              {isError ? "✕" : "✓"}
            </div>
            <div>
              <p className="success-title">
                {isError ? "Error" : "Success"}
              </p>
              <p className="success-text">{successMessage}</p>
            </div>
            {/* FIXED: Manual dismiss button for errors (no auto-dismiss) */}
            <button
              className="notification-close"
              onClick={() => setShowSuccess(false)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Profile Container */}
      <div className={`profile-container ${isFlipped ? "flipped" : ""}`}>
        {/* PROFILE VIEW - FRONT */}
        <div className="profile-card profile-front">
          <div className="profile-header">
            <div className="avatar-circle">
              {user.profileImage ? (
                <img src={user.profileImage} alt="Profile" />
              ) : (
                <span className="avatar-initials">{getInitials()}</span>
              )}
            </div>
            <h2 className="profile-name">
              {formData.firstName} {formData.lastName}
            </h2>
            <p className="profile-email">{formData.email}</p>
          </div>

          <div className="form-header">
            <h3 className="Profile-title">My Profile</h3>
          </div>

          <div className="profile-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-wrapper">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} readOnly className="readonly" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="input-wrapper">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} readOnly className="readonly" />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <input type="email" id="email" name="email" value={formData.email} readOnly className="readonly" />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Mobile Number</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
                <input type="tel" id="phoneNo" name="phoneNo" value={formData.phoneNo} readOnly className="readonly" />
              </div>
            </div>

            <div className="form-actions">
              <button onClick={() => setIsFlipped(true)} className="password-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* CHANGE PASSWORD VIEW - BACK */}
        <div className="profile-card profile-back">
          <div className="back-header">
            {/* FIXED: Disable back button while submitting */}
            <button
              onClick={() => !isSubmitting && setIsFlipped(false)}
              className="back-arrow"
              disabled={isSubmitting}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <h3 className="form-title">Change Password</h3>
          </div>

          <div className="security-tip">
            <svg className="tip-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <p>
              <strong>Security Tip:</strong> Choose a strong password with uppercase, lowercase, numbers, and symbols.
            </p>
          </div>

          <div className="profile-form password-form">
            <div className="form-group">
              <label htmlFor="oldPassword">Current Password</label>
              <div className="input-wrapper password-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type={showOldPass ? "text" : "password"}
                  id="oldPassword"
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  // FIXED: Disable input while submitting
                  disabled={isSubmitting}
                />
                <button type="button" onClick={() => setShowOldPass(!showOldPass)} className="toggle-password">
                  {showOldPass ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="input-wrapper password-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type={showNewPass ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  // FIXED: Disable input while submitting
                  disabled={isSubmitting}
                />
                <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="toggle-password">
                  {showNewPass ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* FIXED: Dynamic password strength meter */}
            {passwordData.newPassword && passwordStrength && (
              <div className="password-strength">
                <div className="strength-header">
                  <span>Password Strength:</span>
                  <span className="strength-level" style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{ width: passwordStrength.width, background: passwordStrength.color }}
                  ></div>
                </div>
              </div>
            )}

            <div className="form-actions">
              {/* FIXED: Button shows loading state while submitting */}
              <button
                onClick={handleSubmitPassword}
                className="save-btn full-width"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn-spinner"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    Update Password
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                onClick={() => !isSubmitting && setIsFlipped(false)}
                className="link-btn"
                disabled={isSubmitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Back to Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
